import get from 'lodash/get';
import { arrayToObject } from '../../utils/transformations';

/**
 * Transforms payment types, to be compatible with flexDropDown syntax
 *
 * @param {object} data datasource, that is coming from API
 * @returns {object}
 */
export const buildPaymentTypesOptions = (data) => {
  let uid = 100;

  return Object.keys(data).reduce((acc, name, id) => {
    const child = data[name].map((item) => {
      uid += 1;
      return {
        id: uid.toString(),
        name: item.description,
        type: item.code
      };
    });
    return acc.concat({ id, name, child });
  }, []);
};

/**
 * Transforms fxRates, to be compatible with flexDropDown syntax
 *
 * @param {object} data datasource, that is coming from API
 * @returns {object}
 */
export const buildFxRatesOptions = (data) => {
  if (!data.find((x) => x.currency === 'USD')) {
    data.unshift({
      currency: 'USD',
      currencyName: 'US Dollar',
      exchangeRate: 1
    });
  }

  return data.map((x, k) => ({
    id: k + 1,
    name: `${x.currency} - ${x.currencyName}`,
    currencyName: x.currencyName,
    currency: x.currency,
    exchange: x.exchangeRate
  }));
};

/**
 * Build 'Amount Type' options for flexDropDown purposes
 * @noparams
 * @returns {object}
 */
export const getAmountTypeOptions = /* istanbul ignore next */ () => [
  {
    id: 1,
    currency: 'USD',
    name: 'USD Equivalent',
    type: 'D'
  },
  {
    id: 2,
    currency: 'FX',
    name: 'Foreign Amount',
    type: 'C'
  }
];

/**
 * Build 'Charges' options for flexDropDown purposes
 * @noparams
 * @returns {object}
 */
export const getChargesOptions = /* istanbul ignore next */ () => [
  {
    id: 'BEN',
    name: 'Beneficiary'
  },
  {
    id: 'OUR',
    name: 'Ours'
  },
  {
    id: 'SHA',
    name: 'Share'
  }
];

/**
 * Prepare dropdown options used in @CreatePaymentForm
 *
 * @param {object} params parameters that are used against function builders
 * @param {object} extra  datasource, that is already loaded and needs to be passed down
 * @returns {object}
 */
export const buildDropdownOptions = ({ fxRates, paymentTypesObj }, extra) => {
  const funcs = arrayToObject(Object.keys(extra).map((v) => ({ [v]: extra[v] })));

  return {
    paymentTypeOptions: buildPaymentTypesOptions(paymentTypesObj),
    chargesOptions: getChargesOptions(),
    fxRates: buildFxRatesOptions(fxRates),
    amountTypes: getAmountTypeOptions(),
    ...funcs
  };
};

/**
 * Map value of dropdown back (1 => Domestic)
 *
 * @param {array} options
 * @param {object} form
 * @param {string} type
 * @returns {object}
 */
export const mapValueBack = (options, { form, matchBy, type }) => {
  const mappings = {
    paymentType: () =>
      [].concat(...options.map((x) => x.child)).find((c) => c[matchBy || 'id'] === form[type])
  };

  if (form[type] && mappings[type]) {
    return mappings[type]();
  }

  return options.find((x) => x[matchBy || 'id'] === get(form, type)) || {};
};

/**
 * TODO: Analyze all the contents in here, do an extract if needed.
 * Returns proper format of amount, based on selection
 *
 * @param {object} form
 * @returns {string}
 */
export const getProperAmount = (form, isView = false, fxRates, data) => {
  const isNum = (prop) => typeof prop === 'number';
  const amountTypes = getAmountTypeOptions();
  const amountType =
    amountTypes.find((x) => x.id === get(form, 'amountType.0.id', get(form, 'amountType'))) ||
    amountTypes[0];
  const currency =
    fxRates &&
    form.currency &&
    mapValueBack(fxRates, {
      form,
      type: 'currency',
      matchBy: isNum(form.currency) ? 'id' : 'currency'
    });

  const isUSD =
    (form.amountType && get(form, 'amountType.0.currency') === 'USD') ||
    form.amountType === 1 ||
    form?.amountType === 'D';
  const amount = form.creditAmount
    ? String(form.creditAmount).replace(/[,]/g, '')
    : form.amount && String(form.amount).replace(/[,]/g, '');

  const formatNum = (num) => parseFloat(String(num).replace(/[,]/g, '')).toFixed(2);
  const hasChanged = (isFX = false, flags = false) => {
    if (!data) {
      return false;
    }

    const changed = {
      amount: formatNum(data[isFX ? 'creditAmount' : 'debitAmount']) !== formatNum(form.amount),
      exchange: data.exchangeRate !== form.exchangeRate,
      amountType: data.amountType !== amountType.id,
      currency: currency && ![currency.id, currency.currency].includes(data.currency)
    };

    const hasChanged =
      changed.currency || changed.amount || changed.exchange || changed.amountType;
    if (flags && hasChanged) {
      return changed;
    }

    return hasChanged;
  };

  const trim = (str) => String(str).replace(/[,]/g, '');

  const getDebitValue = () => {
    const isFX = amountType.currency !== 'USD' || form?.amountType === 'C';
    if (isFX && Number(form.exchangeRate) === 0) {
      return 0;
    }
    const calculated = amount && parseFloat(trim(form.amount) * form.exchangeRate).toFixed(2);

    if (isView) {
      const changed = hasChanged(isFX, true);
      const prop = !isFX ? 'debitAmount' : 'creditAmount';
      if (data && (changed.amount || changed.exchange)) {
        if (formatNum(data[prop]) !== formatNum(form.amount)) {
          return !isFX ? formatNum(form.amount) : calculated;
        }

        if (form.exchangeRate !== data.exchangeRate) {
          return !isFX ? formatNum(form.amount) : calculated;
        }
      }

      return form.debitAmount;
    }

    if (isUSD) return amount;
    return amount && parseFloat(amount * form.exchangeRate).toFixed(2);
  };

  const getSentValue = () => {
    const isFX =
      get(amountType, '0.currency', get(amountType, 'currency')) !== 'USD' ||
      form?.amountType === 'C';
    if (isFX && Number(form.exchangeRate) === 0) {
      return 0;
    }

    const USDEquiv =
      get(form, 'amountType.0.currency') === 'USD' ||
      form.amountType === 1 ||
      form?.amountType === 'D';
    const calculated =
      form.amount && parseFloat((trim(form.amount) / form.exchangeRate).toFixed(2));

    if (isView) {
      const changed = hasChanged(isFX, true);
      const prop = isUSD ? 'debitAmount' : 'creditAmount';
      if (data && (changed.amount || changed.exchange)) {
        if (formatNum(data[prop]) !== formatNum(form.amount)) {
          return isFX ? formatNum(form.amount) : calculated;
        }

        if (form.exchangeRate !== data.exchangeRate) {
          return !isFX ? calculated : formatNum(form.amount);
        }
      }

      return form.creditAmount || form.amount;
    }

    if (USDEquiv && form.exchangeRate) {
      return calculated;
    }

    return amount;
  };

  const getAmount = () => {
    if (isUSD) return amount && amount / form.exchangeRate;
    return amount;
  };

  const response = {
    isUSD,
    amount: Number(getAmount()),
    debit: Number(getDebitValue()),
    sent: Number(getSentValue())
  };

  return {
    isUSD: response.isUSD,
    amount: Number(response.amount && response.amount.toFixed(2)) || 0,
    debit: Number(response.debit && response.debit.toFixed(2)) || 0,
    sent: Number(response.sent && response.sent.toFixed(2)) || 0
  };
};
